import { Button, CircularProgress, Grid, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { KeyboardDatePicker } from '@material-ui/pickers'
import * as Common from '@tfo/common'
import { Elements, SearchInput } from '@tfo/common'
import { ArchivedGroupOrder, ArchivedGroupOrderRow, ArchivedReport, ArchivedReportPaginatedViewModel } from '@tfo/common/src/interfaces'
import classnames from 'classnames'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useHistory } from 'react-router-dom'
import ExcelIcon from '../../../../components/icons/ExcelIcon'
import $http from '../../../../utils/$http'
import ServerSideTable, { Column } from '../../common/ServerSideTable'
import ExcelExportStatus from './ExcelExportStatus'
import { useArchiveExcelExport } from './useArchiveExcelExport'

const useStyles = makeStyles({
    archive__container: {
        padding: '1em'
    },
    contentPadding: {
        display: 'grid',
        gridTemplateColumns: '6fr 0.5fr',
        paddingTop: '1em',
        paddingBottom: '1em'

    },
    topMargin: {
        marginTop: '2rem'
    },
    archive__searchAndFilter: {
        display: 'grid',
        gridTemplateColumns: '6fr 2fr 2fr 2fr 1fr',
        marginBottom: '30px',
        marginTop: '8px',
        "@media (max-width: 768px)": {
            display: 'grid',
            gridTemplateColumns: '6fr 4fr 4fr',
        },
    },
    archive__orderFilter: {
        marginTop: '12px'
    },
    archive__datePickers: {
        marginLeft: '8px',
        width: '200px',
        "@media (max-width: 768px)": {
            marginLeft: '22px'
        },
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                border: '1px solid rbg(223, 223, 222)',
            },
            '&:hover fieldset': {
                border: '1px solid rbg(223, 223, 222)',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'transparent',
                boxShadow: '0 16px 32px 0 rgba(0, 0, 0, 0.06)',
            },
        },

    },
    archive__excelBtn: {
        width: '8px',
        height: '50px',
        "@media (max-width: 799px)": {
            marginTop: '30px',
        },
    },
    archive__h4: {
        letterSpacing: '0.3px',
        marginLeft: '20px',
        fontWeight: 'lighter',
    },
    archive__exportButtonWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
})

const { Loader } = Common
const Archive = () => {
    const store = Common.hooks.useStore()
    const { clientViewStore } = store

    const history = useHistory()
    const intialPage: number = 0
    const initalNumberofRows = 50
    const singleOrderTab = 0
    const groupOrderTab = 1
    const organizationOrderTab = 2
    const defaultColumn: Column[] = [{ label: 'reportPackage', prop: 'reportPackageName' }, { label: 'creator', prop: 'creatorName' }, { label: 'individualId', prop: 'controlPerson' }, { label: 'individualReference', prop: 'individualReference' }, { label: 'ordered', prop: 'created' }, { label: 'delivered', prop: 'delivered' }]
    const defaultColumnGroup: Column[] = [{ label: 'reportPackage', prop: 'reportPackage' }, { label: 'creator', prop: 'creatorName' }, { label: 'noOfIndividuals', prop: 'reportCount' }, { label: 'ordered', prop: 'created' }, { label: 'delivered', prop: 'delivered' }, { label: 'reportWithDiscrepancy', prop: 'discrepancyRatio' }]
    const [columns, setColumns] = React.useState<Column[]>(defaultColumn)
    const [currentPageIndex, setCurrentPageIndex] = React.useState<number>(intialPage)
    const [numberOfRows, setNumberOfRows] = React.useState<number>(initalNumberofRows)
    const [searchString, setSearchString] = React.useState<string>("")
    const [sortBy, setSortBy] = React.useState<string>("")
    const [rows, setRows] = React.useState<ArchivedReport[] | ArchivedGroupOrderRow[]>([])
    const [isLoading, setIsLoading] = React.useState<boolean>(false)
    const [totalRowCount, setTotalRowCount] = React.useState<number>(0)
    const [startDate, setStartDate] = React.useState<Date | null>(null)
    const [endDate, setEndDate] = React.useState<Date>(new Date())
    const [orderTypeFilter, setOrderTypeFilter] = React.useState<number>(singleOrderTab)
    const [groupOrderExist, setGroupOrderExist] = React.useState<boolean>(false)

    const intl = useIntl()

    const companyNames = new Set<string>()
    companyNames.add(clientViewStore.company.name)
    const style = useStyles()

    const { Label } = Elements

    React.useEffect(() => {
        setIsLoading(() => true)
    }, [])

    React.useEffect(() => {
        const timerId = setTimeout(() => {
            switch (orderTypeFilter) {
                case singleOrderTab:
                    getArchivedReports(startDate, endDate, searchString, sortBy, currentPageIndex, numberOfRows)
                    break
                case groupOrderTab:
                    getArchivedGroupOrders(startDate, endDate, searchString, sortBy, currentPageIndex, numberOfRows)
                    break
                case organizationOrderTab:
                    getArchivedOrganizationReports(startDate, endDate, searchString, sortBy, currentPageIndex, numberOfRows)
                    break
                default:
                    break
            }
        }, 200)

        return () => {
            clearTimeout(timerId)
        }
    }, [startDate, endDate, searchString, sortBy, orderTypeFilter, currentPageIndex, numberOfRows])

    const getArchivedReports = (startDate: Date, endDate: Date, searchText: string, sortBy: string, pageIndex: number, rowsPerPage: number): void => {
        $http.post<ArchivedReportPaginatedViewModel<ArchivedReport>>(`/ReportArchive/search?searchText=${searchText}&sortBy=${sortBy}&currentPage=${pageIndex + 1}&startDate=${startDate !== null ? startDate.toJSON() : ''}&endDate=${endDate.toJSON()}&orderGroupingFilter=2&rowsPerPage=${rowsPerPage}`).then((response) => {
            const hasMultipleCompanies: boolean = response.hasMultipleCompanies
            const mappedRows: ArchivedReport[] = response.items.map((i) => {
                companyNames.add(i.companyName)
                return {
                    orderId: i.orderId,
                    companyName: i.companyName,
                    creatorName: i.creatorName,
                    controlPerson: i.controlPerson,
                    individualReference: i.individualReference,
                    created: i.created ? intl.formatDate(i.created) : '',
                    delivered: i.delivered ? intl.formatDate(i.delivered) : '-',
                    reportPackageName: i.reportPackageName,
                    depersonalizationReason: i.depersonalizationReason,
                    creatorId: i.creatorId,
                    orderGroupingFilter: i.orderGroupingFilter
                }
            })
            setGroupOrderExist(response.hasGroupOrders)
            setCurrentPageIndex(response.currentPage - 1)
            setRows(() => mappedRows)
            setTotalRowCount(response.totalRowCount)
            const updatedColumns = hasMultipleCompanies ? [{ label: 'company', prop: 'companyName' }, ...defaultColumn] : defaultColumn
            setColumnDirection(updatedColumns, response.sortBy)
            setColumns(() => updatedColumns)
            setTimeout(() => {
                setIsLoading(() => false)
            }, 1000)
        }).catch((errorMessage) => {
            setRows(() => [])
            console.error(errorMessage)
        })
    }

    const getArchivedGroupOrders = (startDate: Date, endDate: Date, searchText: string, sortBy: string, pageIndex: number, rowsPerPage: number): void => {
        $http.post<ArchivedReportPaginatedViewModel<ArchivedGroupOrder>>(`/ReportArchive/search/group?searchText=${searchText}&sortBy=${sortBy}&currentPage=${pageIndex + 1}&startDate=${startDate !== null ? startDate.toJSON() : ''}&endDate=${endDate.toJSON()}&rowsPerPage=${rowsPerPage}`).then((response) => {
            const hasMultipleCompanies: boolean = response.hasMultipleCompanies
            const mappedRows: ArchivedGroupOrderRow[] = response.items.map((i) => {
                companyNames.add(i.companyName)
                return {
                    id: i.id,
                    reportCount: i.reportCount,
                    companyId: i.companyId,
                    companyName: i.companyName,
                    reportPackage: i.reportPackage,
                    creatorName: i.creatorName,
                    created: i.created ? intl.formatDate(i.created) : '',
                    delivered: i.delivered ? intl.formatDate(i.delivered) : '',
                    discrepancyRatio: `${i.reportWithDiscrepancyCount}/${i.reportCount}`,
                }
            })
            setGroupOrderExist(response.hasGroupOrders)
            setCurrentPageIndex(response.currentPage - 1)
            setRows(() => mappedRows)
            setTotalRowCount(response.totalRowCount)
            const updatedColumns = hasMultipleCompanies ? [{ label: 'company', prop: 'companyName' }, ...defaultColumnGroup] : defaultColumnGroup
            setColumnDirection(updatedColumns, response.sortBy)
            setColumns(() => updatedColumns)
            setTimeout(() => {
                setIsLoading(() => false)
            }, 1000)
        }).catch((errorMessage) => {
            setRows(() => [])
            console.error(errorMessage)
        })
    }

    const getArchivedOrganizationReports = (startDate: Date, endDate: Date, searchText: string, sortBy: string, pageIndex: number, rowsPerPage: number): void => {
        $http.post<ArchivedReportPaginatedViewModel<ArchivedReport>>(`/ReportArchive/search/concern?searchText=${searchText}&sortBy=${sortBy}&currentPage=${pageIndex + 1}&startDate=${startDate !== null ? startDate.toJSON() : ''}&endDate=${endDate.toJSON()}&orderGroupingFilter=2&rowsPerPage=${rowsPerPage}`).then((response) => {
            const hasMultipleCompanies: boolean = response.hasMultipleCompanies
            const mappedRows: ArchivedReport[] = response.items.map((i) => {
                companyNames.add(i.companyName)
                return {
                    orderId: i.orderId,
                    companyName: i.companyName,
                    creatorName: i.creatorName,
                    controlPerson: i.controlPerson,
                    individualReference: i.individualReference,
                    created: i.created ? intl.formatDate(i.created) : '',
                    delivered: i.delivered ? intl.formatDate(i.delivered) : '-',
                    reportPackageName: i.reportPackageName,
                    depersonalizationReason: i.depersonalizationReason,
                    creatorId: i.creatorId,
                    orderGroupingFilter: i.orderGroupingFilter
                }
            })
            setGroupOrderExist(response.hasGroupOrders)
            setCurrentPageIndex(response.currentPage - 1)
            setRows(() => mappedRows)
            setTotalRowCount(response.totalRowCount)
            const updatedColumns = hasMultipleCompanies ? [{ label: 'company', prop: 'companyName' }, ...defaultColumn] : defaultColumn
            setColumnDirection(updatedColumns, response.sortBy)
            setColumns(() => updatedColumns)
            setTimeout(() => {
                setIsLoading(() => false)
            }, 1000)
        }).catch((errorMessage) => {
            setRows(() => [])
            console.error(errorMessage)
        })
    }

    const setColumnDirection = (columns: Column[], sortBy: string): void => {
        const [name, direction] = sortBy.split('-')
        const column = columns.find(x => x.prop === name)
        const index = columns.indexOf(column)
        columns[index]._direction = direction == 'desc' ? 'desc' : 'asc'
    }

    const onChangePage = (event: unknown, pageIndex: number): void => {
        setCurrentPageIndex(pageIndex)
    }

    const onChangeNumberOfRows = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const updatedNumberOfRows: number = +event.target.value
        setNumberOfRows(() => updatedNumberOfRows)
        setCurrentPageIndex(0)
    }

    const onChangeSortOrder = (direction: 'asc' | 'desc', prop: string): void => {
        const newSortBy = direction == 'asc' ? prop : prop + '-desc'
        setSortBy(newSortBy)
    }

    const onClearSearch = (): void => {
        setSearchString(() => '')
        setCurrentPageIndex(0)
        getArchivedReports(startDate, endDate, '', sortBy, 0, numberOfRows)
    }

    const onInputSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchString(e.target.value)
    }

    const { isExporting, exportError, exportProgress, showProgressBar, exportSingleOrders, exportGroupOrders, exportOrganizationOrders } = useArchiveExcelExport({
        searchString,
        sortBy,
        startDate,
        endDate,
        intl,
    })

    const exportHandlersByTab: Record<number, () => Promise<void>> = {
        [singleOrderTab]: exportSingleOrders,
        [groupOrderTab]: exportGroupOrders,
        [organizationOrderTab]: exportOrganizationOrders,
    }

    return (
        <>
            <div className={'ClientContainer' + (isLoading ? ('--loading') : '')}>
                {
                    isLoading ? <Loader></Loader> : <Grid
                        container
                        justifyContent="center"
                        direction="column"
                        className={['GridWrapper', style.topMargin].join(' ')}>
                        <div
                            className={
                                classnames(
                                    'Reports',
                                    {
                                        'Reports--empty': rows.length <= 0 && !isLoading
                                    },
                                )}>

                            <Grid className={[style.archive__container, 'container-fluid'].join(' ')}>
                                <div className={style.contentPadding}>
                                    <h1 className='Heading1'>{intl.formatMessage({ id: 'archived.reports.title' })}</h1>
                                </div>
                                <div className="ReportsFiltering ReportsFilteringMenu">
                                    <Label
                                        className={classnames('ReportsFiltering__Item', {
                                            'ReportsFiltering__Item--active': orderTypeFilter === singleOrderTab,
                                        })}
                                        onClick={() => setOrderTypeFilter(singleOrderTab)}>
                                        {intl.formatMessage({ id: 'archived.report.category.label.filtering.singleOrders' })}
                                    </Label>
                                    <Label
                                        className={classnames('ReportsFiltering__Item', {
                                            'ReportsFiltering__Item--active': orderTypeFilter === groupOrderTab,
                                        })}
                                        onClick={() => setOrderTypeFilter(groupOrderTab)}>
                                        {intl.formatMessage({ id: 'archived.report.category.label.filtering.groupOrders' })}
                                    </Label>
                                    {store.userStore.user.roles.some(x => x.role === 'SuperUser') &&
                                        <Label
                                            className={classnames('ReportsFiltering__Item', {
                                                'ReportsFiltering__Item--active': orderTypeFilter === organizationOrderTab,
                                            })}
                                            onClick={() => setOrderTypeFilter(organizationOrderTab)}>
                                            {intl.formatMessage({ id: 'archived.report.category.label.filtering.organizationOrders' })}
                                        </Label>
                                    }
                                </div>
                                <Grid container>
                                    <div className={style.archive__searchAndFilter}>
                                        <SearchInput
                                            value={searchString}
                                            onInputChange={onInputSearch}
                                            onClear={onClearSearch}
                                            placeholder={intl.formatMessage({ id: 'search.individualId' })}
                                        />
                                        <KeyboardDatePicker
                                            className={style.archive__datePickers}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disableToolbar
                                            inputVariant="outlined"
                                            label={intl.formatMessage({ id: 'common.startDate' })}
                                            format='yyyy-MM-dd'
                                            maxDate={new Date}
                                            value={startDate}
                                            onChange={e => setStartDate(e)} />
                                        <KeyboardDatePicker
                                            className={style.archive__datePickers}
                                            disableToolbar
                                            inputVariant="outlined"
                                            label={intl.formatMessage({ id: 'common.endDate' })}
                                            format='yyyy-MM-dd'
                                            minDate={startDate}
                                            value={endDate < startDate ? '' : endDate}
                                            onChange={e => setEndDate(e)} />

                                        <h4 className={style.archive__h4}>{intl.formatMessage({ id: 'found.items' }) + ' '}<b>{totalRowCount}</b></h4>
                                        <div className={style.archive__exportButtonWrapper}>
                                            <Tooltip title={intl.formatMessage({ id: 'download.excel' })}>
                                                <span>
                                                    <Button
                                                        className={style.archive__excelBtn}
                                                        disabled={isExporting}
                                                        onClick={() => exportHandlersByTab[orderTypeFilter]()}>
                                                        {showProgressBar ? <CircularProgress size={20} /> : <ExcelIcon />}
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </div>
                                    </div>
                                </Grid>
                                <ExcelExportStatus
                                    exportError={exportError}
                                    isExporting={isExporting}
                                    showProgressBar={showProgressBar}
                                    exportProgress={exportProgress} />
                                {isLoading ? <Loader></Loader> :
                                    <Grid container direction="column" >
                                        <Grid container>
                                            <ServerSideTable
                                                columns={columns}
                                                rows={rows}
                                                onRowClick={(row) => orderTypeFilter === 1 ? history.push(`archived-group-order/${row.id}`) : history.push(`archived-report/${row.orderId}`)}
                                                pageIndex={currentPageIndex} onChangePage={onChangePage} numberOfRows={numberOfRows}
                                                totalRowCount={totalRowCount}
                                                onChangeNumberOfRows={onChangeNumberOfRows}
                                                onChangeSortOrder={onChangeSortOrder} />
                                        </Grid>
                                    </Grid>
                                }
                            </Grid>
                        </div>
                    </Grid>
                }
            </div>
        </>
    )
}
export default Archive
